import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import {
    Box, Typography, Card, Stack, Grid, Button as MuiButton,
    TextField, MenuItem, Chip, Divider, IconButton, Alert, Stepper, Step, StepLabel
} from '@mui/material';
import { ArrowLeft, Plus, Trash2, Volume2, Calendar, Target, CheckCircle2, Sparkles, Edit2 } from 'lucide-react';

// Campaign stored in localStorage (until DB table is created)
const STORAGE_KEY = 'postpilot_campaigns';

function loadCampaigns(): Campaign[] {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); } catch { return []; }
}
function saveCampaigns(campaigns: Campaign[]) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(campaigns));
}

interface Campaign {
    id: string;
    name: string;
    platform: string;
    postType: string;
    tone: string;
    audience: string;
    frequency: string;
    startDate: string;
    endDate: string;
    topic: string;
    status: 'Active' | 'Paused' | 'Completed';
    postsPlanned: number;
    createdAt: string;
}

const PLATFORMS = ['Instagram', 'LinkedIn', 'Twitter/X', 'Facebook', 'TikTok', 'YouTube'];
const POST_TYPES = ['Caption Post', 'Carousel Post', 'Hook Post'];
const TONES = ['Professional', 'Casual', 'Inspirational', 'Humorous', 'Educational'];
const AUDIENCES = ['Entrepreneurs', 'Developers', 'Marketers', 'Designers', 'General Audience', 'Students'];
const FREQUENCIES = ['Daily', '3x per week', 'Weekly', 'Bi-weekly'];

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
    Active: { bg: '#dcfce7', color: '#166534' },
    Paused: { bg: '#fef9c3', color: '#854d0e' },
    Completed: { bg: '#f1f5f9', color: '#475569' },
};

export default function Campaigns() {
    const navigate = useNavigate();
    const { posts } = useAppContext();
    const [campaigns, setCampaigns] = useState<Campaign[]>(loadCampaigns);
    const [creating, setCreating] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const [saved, setSaved] = useState(false);

    const [form, setForm] = useState({
        name: '', platform: '', postType: '', tone: '', audience: '',
        frequency: '', startDate: '', endDate: '', topic: '',
    });

    const set = (field: string, val: string) => setForm(prev => ({ ...prev, [field]: val }));

    const steps = ['Campaign Details', 'Content Settings', 'Schedule'];

    const handleCreate = () => {
        if (!form.name || !form.platform || !form.topic) return;

        // Estimate posts planned from frequency + date range
        const start = new Date(form.startDate || Date.now());
        const end = new Date(form.endDate || Date.now());
        const days = Math.max(1, Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)));
        const freqMap: Record<string, number> = { 'Daily': 1, '3x per week': 3 / 7, 'Weekly': 1 / 7, 'Bi-weekly': 1 / 14 };
        const postsPlanned = Math.max(1, Math.round(days * (freqMap[form.frequency] || 1)));

        const newCampaign: Campaign = {
            id: Date.now().toString(),
            ...form,
            status: 'Active',
            postsPlanned,
            createdAt: new Date().toISOString(),
        };
        const updated = [newCampaign, ...campaigns];
        saveCampaigns(updated);
        setCampaigns(updated);
        setSaved(true);
        setCreating(false);
        setActiveStep(0);
        setForm({ name: '', platform: '', postType: '', tone: '', audience: '', frequency: '', startDate: '', endDate: '', topic: '' });
        setTimeout(() => setSaved(false), 3000);
    };

    const toggleStatus = (id: string) => {
        const updated = campaigns.map(c =>
            c.id === id ? { ...c, status: c.status === 'Active' ? 'Paused' as const : 'Active' as const } : c
        );
        saveCampaigns(updated);
        setCampaigns(updated);
    };

    const deleteCampaign = (id: string) => {
        const updated = campaigns.filter(c => c.id !== id);
        saveCampaigns(updated);
        setCampaigns(updated);
    };

    const canNextStep0 = form.name && form.platform && form.topic;
    const canNextStep1 = form.postType && form.tone && form.audience;
    const canSubmit = canNextStep0 && canNextStep1 && form.frequency;

    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                <IconButton onClick={() => navigate(-1)} sx={{ bgcolor: '#f1f5f9', '&:hover': { bgcolor: '#e2e8f0' }, borderRadius: 2 }}>
                    <ArrowLeft size={18} color="#334155" />
                </IconButton>
                <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h5" fontWeight={800} color="#111827" letterSpacing="-0.02em">Campaigns</Typography>
                    <Typography variant="body2" color="#64748b">Organise your content into scheduled campaigns</Typography>
                </Box>
                {!creating && (
                    <MuiButton
                        variant="contained"
                        startIcon={<Plus size={16} />}
                        onClick={() => setCreating(true)}
                        sx={{ bgcolor: '#4f46e5', textTransform: 'none', fontWeight: 700, borderRadius: 2, boxShadow: 'none', '&:hover': { bgcolor: '#4338ca' } }}
                    >
                        New Campaign
                    </MuiButton>
                )}
            </Box>

            {saved && <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>Campaign created successfully!</Alert>}

            {/* Create Campaign Form */}
            {creating && (
                <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #e2e8f0', p: 4, mb: 4 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                        <Typography variant="h6" fontWeight={700} color="#111827">Create New Campaign</Typography>
                        <MuiButton onClick={() => { setCreating(false); setActiveStep(0); }} sx={{ textTransform: 'none', color: '#64748b', fontWeight: 600 }}>Cancel</MuiButton>
                    </Box>

                    <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                        {steps.map(label => (
                            <Step key={label}><StepLabel>{label}</StepLabel></Step>
                        ))}
                    </Stepper>

                    {activeStep === 0 && (
                        <Grid container spacing={3}>
                            <Grid size={{ xs: 12 }}>
                                <TextField fullWidth label="Campaign Name" value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Q2 Product Launch" sx={{ borderRadius: 2 }} />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField fullWidth select label="Platform" value={form.platform} onChange={e => set('platform', e.target.value)}>
                                    {PLATFORMS.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                                </TextField>
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField fullWidth label="Topic / Theme" value={form.topic} onChange={e => set('topic', e.target.value)} placeholder="e.g. AI Productivity, Brand Awareness" />
                            </Grid>
                        </Grid>
                    )}

                    {activeStep === 1 && (
                        <Grid container spacing={3}>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <TextField fullWidth select label="Post Type" value={form.postType} onChange={e => set('postType', e.target.value)}>
                                    {POST_TYPES.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                                </TextField>
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <TextField fullWidth select label="Tone" value={form.tone} onChange={e => set('tone', e.target.value)}>
                                    {TONES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                                </TextField>
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <TextField fullWidth select label="Target Audience" value={form.audience} onChange={e => set('audience', e.target.value)}>
                                    {AUDIENCES.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                                </TextField>
                            </Grid>
                        </Grid>
                    )}

                    {activeStep === 2 && (
                        <Grid container spacing={3}>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <TextField fullWidth select label="Posting Frequency" value={form.frequency} onChange={e => set('frequency', e.target.value)}>
                                    {FREQUENCIES.map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
                                </TextField>
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <TextField fullWidth type="date" label="Start Date" value={form.startDate} onChange={e => set('startDate', e.target.value)} InputLabelProps={{ shrink: true }} />
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <TextField fullWidth type="date" label="End Date" value={form.endDate} onChange={e => set('endDate', e.target.value)} InputLabelProps={{ shrink: true }} />
                            </Grid>
                        </Grid>
                    )}

                    <Stack direction="row" justifyContent="space-between" sx={{ mt: 4 }}>
                        <MuiButton disabled={activeStep === 0} onClick={() => setActiveStep(s => s - 1)} sx={{ textTransform: 'none', fontWeight: 600 }}>Back</MuiButton>
                        {activeStep < steps.length - 1 ? (
                            <MuiButton
                                variant="contained"
                                disabled={activeStep === 0 ? !canNextStep0 : !canNextStep1}
                                onClick={() => setActiveStep(s => s + 1)}
                                sx={{ bgcolor: '#4f46e5', textTransform: 'none', fontWeight: 700, borderRadius: 2, boxShadow: 'none' }}
                            >
                                Next
                            </MuiButton>
                        ) : (
                            <MuiButton
                                variant="contained"
                                disabled={!canSubmit}
                                onClick={handleCreate}
                                startIcon={<CheckCircle2 size={16} />}
                                sx={{ bgcolor: '#10b981', textTransform: 'none', fontWeight: 700, borderRadius: 2, boxShadow: 'none', '&:hover': { bgcolor: '#059669' } }}
                            >
                                Create Campaign
                            </MuiButton>
                        )}
                    </Stack>
                </Card>
            )}

            {/* Campaign List */}
            {campaigns.length === 0 && !creating ? (
                <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #e2e8f0', p: 8, textAlign: 'center' }}>
                    <Volume2 size={48} color="#94a3b8" style={{ margin: '0 auto 16px' }} />
                    <Typography variant="h6" fontWeight={700} color="#111827" gutterBottom>No campaigns yet</Typography>
                    <Typography variant="body2" color="#64748b" sx={{ mb: 3, maxWidth: 320, mx: 'auto' }}>
                        Campaigns help you organise your content into scheduled series for consistent posting.
                    </Typography>
                    <MuiButton variant="contained" startIcon={<Plus size={16} />} onClick={() => setCreating(true)}
                        sx={{ bgcolor: '#4f46e5', textTransform: 'none', fontWeight: 700, borderRadius: 2, boxShadow: 'none' }}>
                        Create First Campaign
                    </MuiButton>
                </Card>
            ) : (
                <Stack spacing={2}>
                    {campaigns.map(c => (
                        <Card key={c.id} elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: '#e0e7ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    <Volume2 size={20} color="#4f46e5" />
                                </Box>
                                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 0.5 }}>
                                        <Typography variant="body1" fontWeight={700} color="#111827">{c.name}</Typography>
                                        <Chip label={c.status} size="small"
                                            sx={{ bgcolor: STATUS_COLORS[c.status]?.bg, color: STATUS_COLORS[c.status]?.color, fontWeight: 700, fontSize: '0.7rem' }} />
                                    </Box>
                                    <Stack direction="row" spacing={1.5} flexWrap="wrap">
                                        {[c.platform, c.postType, c.tone, c.audience].filter(Boolean).map((tag, i) => (
                                            <Typography key={i} variant="caption" color="#64748b" fontWeight={600}>{tag}</Typography>
                                        ))}
                                    </Stack>
                                    <Stack direction="row" spacing={3} sx={{ mt: 1.5 }} flexWrap="wrap">
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                            <Target size={13} color="#94a3b8" />
                                            <Typography variant="caption" color="#94a3b8">{c.topic}</Typography>
                                        </Stack>
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                            <Sparkles size={13} color="#94a3b8" />
                                            <Typography variant="caption" color="#94a3b8">{c.frequency} · {c.postsPlanned} posts planned</Typography>
                                        </Stack>
                                        {c.startDate && (
                                            <Stack direction="row" spacing={0.5} alignItems="center">
                                                <Calendar size={13} color="#94a3b8" />
                                                <Typography variant="caption" color="#94a3b8">{c.startDate} → {c.endDate || 'ongoing'}</Typography>
                                            </Stack>
                                        )}
                                    </Stack>
                                </Box>
                                <Stack direction="row" spacing={1} flexShrink={0}>
                                    <MuiButton size="small" variant="outlined" onClick={() => toggleStatus(c.id)}
                                        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, borderColor: '#e2e8f0', color: '#334155', fontSize: '0.75rem' }}>
                                        {c.status === 'Active' ? 'Pause' : 'Resume'}
                                    </MuiButton>
                                    <MuiButton size="small" variant="contained" startIcon={<Sparkles size={13} />}
                                        onClick={() => navigate('/dashboard/generate')}
                                        sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, bgcolor: '#4f46e5', boxShadow: 'none', fontSize: '0.75rem' }}>
                                        Generate
                                    </MuiButton>
                                    <IconButton size="small" onClick={() => deleteCampaign(c.id)} sx={{ color: '#ef4444', '&:hover': { bgcolor: '#fee2e2' } }}>
                                        <Trash2 size={16} />
                                    </IconButton>
                                </Stack>
                            </Box>
                        </Card>
                    ))}
                </Stack>
            )}
        </Box>
    );
}
